import type { MusicSpec, Theme } from '../../shared/themes';
import type { ThemeId } from '../../shared/types';

export type SfxName =
  | 'jump' | 'land' | 'landHard' | 'orb' | 'gear' | 'boost' | 'send' | 'sendEnd'
  | 'crash' | 'nearMiss' | 'trickStart' | 'trickLand' | 'sketchy'
  | 'comboUp' | 'comboBreak' | 'finish' | 'menuMove' | 'menuSelect'
  | 'countdown' | 'go' | 'ratchet';

type AudioContextCtor = typeof AudioContext;

const AC: AudioContextCtor | undefined =
  typeof window === 'undefined'
    ? undefined
    : window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;

/** Scheduler tick period and how far ahead of the clock each tick fills in. */
const TICK_MS = 25;
const LOOKAHEAD = 0.12;
const CROSSFADE = 0.6;
const MASTER_LEVEL = 0.75;
/** Two SFX of the same name closer than this collapse into one. */
const SFX_GAP = 0.03;
/** exponentialRamp cannot reach zero. */
const SILENT = 0.0001;

/** Scale-degree indices walked by the lead; fixed so the arpeggio is a riff, not noise. */
const LEAD_PATTERN = [0, 2, 4, 2, 5, 4, 7, 4];
const MAJOR_FALLBACK = [0, 2, 4, 5, 7, 9, 11];

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : Number.isFinite(v) ? v : 0;
}

/** Semitone offset of `index` degrees up a scale, wrapping into higher octaves. */
function scaleFreq(base: number, scale: number[], index: number): number {
  const n = scale.length;
  if (n === 0) return base;
  const oct = Math.floor(index / n);
  const st = scale[((index % n) + n) % n] ?? 0;
  return base * Math.pow(2, (st + 12 * oct) / 12);
}

function leadLevel(intensity: number): number {
  return intensity <= 0.35 ? 0 : ((intensity - 0.35) / 0.65) * 0.13;
}

function padCutoff(intensity: number): number {
  return 320 + intensity * 2400;
}

interface MusicVoice {
  themeId: ThemeId;
  spec: MusicSpec;
  out: GainNode;
  dry: GainNode;
  send: GainNode;
  padFilter: BiquadFilterNode;
  leadGain: GainNode;
  nodes: AudioNode[];
}

export class AudioEngine {
  readonly supported: boolean;

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  private unlockedFlag = false;
  private mutedFlag = false;
  private unlocking: Promise<void> | null = null;

  private voice: MusicVoice | null = null;
  private spec: MusicSpec | null = null;
  private pending: Theme | null = null;

  private timer = 0;
  private retireTimers = new Set<number>();
  private step = 0;
  private bar = 0;
  private nextStepTime = 0;

  private musicVolume = 1;
  private intensityTarget = 0;
  private intensityNow = 0;

  private speedTarget = 0;
  private speedSrc: AudioBufferSourceNode | null = null;
  private speedFilter: BiquadFilterNode | null = null;
  private speedGain: GainNode | null = null;

  private noiseBuf: AudioBuffer | null = null;
  private lastSfx = new Map<SfxName, number>();
  private lastResumeAttempt = 0;
  private disposed = false;

  constructor() {
    this.supported = AC !== undefined;
  }

  get unlocked(): boolean {
    return this.unlockedFlag;
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  async unlock(): Promise<void> {
    if (!this.supported || this.disposed) return;
    if (this.unlockedFlag) {
      this.resume();
      return;
    }
    if (this.unlocking) return this.unlocking;
    this.unlocking = this.doUnlock().finally(() => {
      this.unlocking = null;
    });
    return this.unlocking;
  }

  /**
   * The OS suspends the context whenever it feels like it — backgrounding, a
   * lock, a phone call. Nothing restarts it on its own, so every path that is
   * about to make a sound nudges it first. Safe and cheap to call constantly.
   */
  resume(): void {
    const ctx = this.ctx;
    if (!ctx || this.disposed) return;
    if (ctx.state === 'running') return;
    const now = Date.now();
    if (now - this.lastResumeAttempt < 250) return;
    this.lastResumeAttempt = now;
    void ctx.resume().catch(() => {
      // Rejected outside a user gesture; the next tap will get it.
    });
  }

  setMuted(m: boolean): void {
    this.mutedFlag = m;
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(m ? 0 : MASTER_LEVEL, t + 0.08);
  }

  startMusic(theme: Theme): void {
    this.spec = theme.music;
    const ctx = this.ctx;
    if (!this.supported || this.disposed || !ctx || !this.unlockedFlag || !this.musicGain) {
      this.pending = theme;
      return;
    }
    this.resume();
    this.pending = null;
    if (this.voice && this.voice.themeId === theme.id) {
      // Same world again. The voice survived, but the scheduler may not have.
      if (this.timer === 0) this.timer = window.setInterval(this.tick, TICK_MS);
      return;
    }

    const now = ctx.currentTime;
    const prev = this.voice;
    const next = this.createVoice(theme, ctx);
    const fade = prev ? CROSSFADE : 0.35;
    next.out.gain.setValueAtTime(SILENT, now);
    next.out.gain.linearRampToValueAtTime(1, now + fade);
    if (prev) this.retireVoice(prev, ctx, CROSSFADE);
    this.voice = next;

    if (!prev) {
      this.step = 0;
      this.bar = 0;
      this.nextStepTime = now + 0.08;
    }
    if (this.timer === 0) this.timer = window.setInterval(this.tick, TICK_MS);
  }

  stopMusic(fadeSeconds = CROSSFADE): void {
    this.pending = null;
    const ctx = this.ctx;
    if (this.timer !== 0) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }
    if (ctx && this.voice) this.retireVoice(this.voice, ctx, Math.max(0.05, fadeSeconds));
    this.voice = null;
    this.stopSpeedNoise();
  }

  setIntensity(v: number): void {
    const c = clamp01(v);
    this.intensityTarget = c;
    const ctx = this.ctx;
    const voice = this.voice;
    if (!ctx || !voice) return;
    const t = ctx.currentTime;
    voice.leadGain.gain.setTargetAtTime(leadLevel(c), t, 0.35);
    voice.padFilter.frequency.setTargetAtTime(padCutoff(c), t, 0.4);
  }

  setSpeedRatio(v: number): void {
    const c = clamp01(v);
    this.speedTarget = c;
    const ctx = this.ctx;
    if (!ctx || !this.unlockedFlag || this.disposed) return;
    if (c > 0 && !this.speedSrc) this.startSpeedNoise(ctx);
    const filter = this.speedFilter;
    const gain = this.speedGain;
    if (!filter || !gain) return;
    const t = ctx.currentTime;
    gain.gain.setTargetAtTime(0.15 * c * c, t, 0.12);
    filter.frequency.setTargetAtTime(220 + c * 2600, t, 0.15);
  }

  setMusicVolume(v: number): void {
    this.musicVolume = clamp01(v);
    const ctx = this.ctx;
    const bus = this.musicGain;
    if (!ctx || !bus) return;
    const t = ctx.currentTime;
    bus.gain.cancelScheduledValues(t);
    bus.gain.setTargetAtTime(this.musicVolume, t, 0.08);
  }

  sfx(name: SfxName, opts?: { rate?: number; gain?: number; pan?: number }): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!this.supported || this.disposed || !ctx || !bus || !this.unlockedFlag) return;
    if (ctx.state !== 'running') {
      this.resume();
      return;
    }
    const t = ctx.currentTime;
    const last = this.lastSfx.get(name);
    if (last !== undefined && t - last < SFX_GAP) return;
    this.lastSfx.set(name, t);

    const rate = opts?.rate !== undefined && opts.rate > 0 ? opts.rate : 1;
    const level = opts?.gain !== undefined ? Math.max(0, opts.gain) : 1;
    const pan = opts?.pan !== undefined ? Math.max(-1, Math.min(1, opts.pan)) : 0;

    const nodes: AudioNode[] = [];
    const srcs: AudioScheduledSourceNode[] = [];

    const out = ctx.createGain();
    out.gain.value = level;
    nodes.push(out);
    if (pan !== 0 && typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      out.connect(p);
      p.connect(bus);
      nodes.push(p);
    } else {
      out.connect(bus);
    }

    const env = (dest: AudioNode, peak: number, start: number, attack: number, decay: number): GainNode => {
      const g = ctx.createGain();
      g.gain.setValueAtTime(SILENT, start);
      g.gain.exponentialRampToValueAtTime(Math.max(peak, SILENT * 2), start + attack);
      g.gain.exponentialRampToValueAtTime(SILENT, start + attack + decay);
      g.connect(dest);
      nodes.push(g);
      return g;
    };
    const filt = (type: BiquadFilterType, freq: number, q: number, dest: AudioNode): BiquadFilterNode => {
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = freq;
      f.Q.value = q;
      f.connect(dest);
      nodes.push(f);
      return f;
    };
    const osc = (type: OscillatorType, freq: number, dest: AudioNode, start: number, stop: number): OscillatorNode => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(freq, start);
      o.connect(dest);
      o.start(start);
      o.stop(stop);
      srcs.push(o);
      return o;
    };
    const noise = (dest: AudioNode, start: number, stop: number): AudioBufferSourceNode => {
      const buf = this.noiseBuffer(ctx);
      const s = ctx.createBufferSource();
      s.buffer = buf;
      s.connect(dest);
      s.start(start, Math.random() * Math.max(0.001, buf.duration - 0.5));
      s.stop(stop);
      srcs.push(s);
      return s;
    };
    const blip = (freq: number, type: OscillatorType, at: number, peak: number, decay: number): void => {
      const g = env(out, peak, at, 0.004, decay);
      osc(type, freq, g, at, at + decay + 0.05);
    };

    switch (name) {
      case 'jump': {
        const g = env(out, 0.3, t, 0.004, 0.12);
        const o = osc('triangle', 240, g, t, t + 0.2);
        o.frequency.exponentialRampToValueAtTime(680, t + 0.11);
        break;
      }
      case 'land': {
        const f = filt('lowpass', 760, 0.9, out);
        const g = env(f, 0.34, t, 0.003, 0.13);
        noise(g, t, t + 0.2);
        break;
      }
      case 'landHard': {
        const f = filt('lowpass', 420, 1.1, out);
        const g = env(f, 0.5, t, 0.003, 0.24);
        noise(g, t, t + 0.32);
        const bg = env(out, 0.42, t, 0.003, 0.22);
        const b = osc('sine', 130, bg, t, t + 0.3);
        b.frequency.exponentialRampToValueAtTime(44, t + 0.12);
        break;
      }
      case 'orb': {
        const base = 880 * rate;
        const g = env(out, 0.22, t, 0.003, 0.16);
        const o = osc('sine', base, g, t, t + 0.24);
        o.frequency.exponentialRampToValueAtTime(base * 1.28, t + 0.09);
        const g2 = env(out, 0.09, t, 0.003, 0.12);
        osc('triangle', base * 2, g2, t, t + 0.2);
        break;
      }
      case 'gear': {
        const steps = [0, 4, 7, 12];
        for (let i = 0; i < steps.length; i++) {
          const at = t + i * 0.075;
          const f = 523.25 * rate * Math.pow(2, (steps[i] ?? 0) / 12);
          const g = env(out, 0.2, at, 0.004, 0.36);
          osc('sine', f, g, at, at + 0.45);
          const g2 = env(out, 0.06, at, 0.004, 0.26);
          osc('sine', f * 2.01, g2, at, at + 0.35);
        }
        break;
      }
      case 'boost': {
        const f = filt('bandpass', 320, 4, out);
        f.frequency.exponentialRampToValueAtTime(4200, t + 0.34);
        const g = env(f, 0.32, t, 0.02, 0.34);
        noise(g, t, t + 0.42);
        break;
      }
      case 'send': {
        const f = filt('bandpass', 220, 2.2, out);
        f.frequency.exponentialRampToValueAtTime(5200, t + 0.55);
        const g = env(f, 0.34, t, 0.05, 0.55);
        noise(g, t, t + 0.68);
        const sg = env(out, 0.16, t, 0.06, 0.52);
        const s = osc('sawtooth', 110, sg, t, t + 0.66);
        s.frequency.exponentialRampToValueAtTime(660, t + 0.55);
        break;
      }
      case 'sendEnd': {
        const g = env(out, 0.22, t, 0.01, 0.34);
        const o = osc('triangle', 540, g, t, t + 0.42);
        o.frequency.exponentialRampToValueAtTime(170, t + 0.32);
        break;
      }
      case 'crash': {
        const f = filt('lowpass', 2400, 0.8, out);
        f.frequency.exponentialRampToValueAtTime(220, t + 0.34);
        const g = env(f, 0.55, t, 0.002, 0.36);
        noise(g, t, t + 0.44);
        const tg = env(out, 0.3, t, 0.003, 0.3);
        osc('square', 62, tg, t, t + 0.38);
        osc('square', 65.5, tg, t, t + 0.38);
        break;
      }
      case 'nearMiss': {
        const f = filt('bandpass', 900, 3, out);
        f.frequency.exponentialRampToValueAtTime(3200, t + 0.15);
        const g = env(f, 0.24, t, 0.006, 0.15);
        noise(g, t, t + 0.22);
        break;
      }
      case 'trickStart': {
        for (let i = 0; i < 8; i++) {
          const at = t + i * 0.028;
          const f = filt('highpass', 1800 + i * 420, 0.8, out);
          const g = env(f, 0.13, at, 0.001, 0.018);
          noise(g, at, at + 0.03);
        }
        break;
      }
      case 'trickLand': {
        blip(659.25, 'triangle', t, 0.24, 0.16);
        blip(987.77, 'triangle', t + 0.07, 0.24, 0.22);
        break;
      }
      case 'sketchy': {
        const f = filt('lowpass', 1300, 1.4, out);
        const g = env(f, 0.2, t, 0.004, 0.2);
        osc('sawtooth', 176, g, t, t + 0.28);
        osc('sawtooth', 187, g, t, t + 0.28);
        break;
      }
      case 'comboUp': {
        const base = 660 * rate;
        blip(base, 'sine', t, 0.2, 0.12);
        blip(base * 2, 'triangle', t, 0.07, 0.1);
        break;
      }
      case 'comboBreak': {
        blip(440, 'triangle', t, 0.2, 0.13);
        blip(369.99, 'triangle', t + 0.1, 0.2, 0.2);
        break;
      }
      case 'finish': {
        const spec = this.spec;
        const scale = spec ? spec.scale : MAJOR_FALLBACK;
        const base = spec ? spec.root * 4 : 523.25;
        const degrees = [0, 2, 4, 7];
        for (let i = 0; i < degrees.length; i++) {
          const at = t + i * 0.13;
          const f = scaleFreq(base, scale, degrees[i] ?? 0);
          const decay = i === degrees.length - 1 ? 0.7 : 0.3;
          const g = env(out, 0.2, at, 0.006, decay);
          osc('triangle', f, g, at, at + decay + 0.1);
          const g2 = env(out, 0.07, at, 0.006, decay);
          osc('sine', f * 2, g2, at, at + decay + 0.1);
        }
        break;
      }
      case 'menuMove': {
        blip(1400 * rate, 'square', t, 0.09, 0.035);
        break;
      }
      case 'menuSelect': {
        blip(880, 'triangle', t, 0.16, 0.1);
        blip(1318.51, 'triangle', t + 0.07, 0.16, 0.16);
        break;
      }
      case 'countdown': {
        blip(660, 'sine', t, 0.26, 0.15);
        break;
      }
      case 'go': {
        const g = env(out, 0.32, t, 0.006, 0.34);
        const o = osc('sine', 880, g, t, t + 0.42);
        o.frequency.exponentialRampToValueAtTime(1180, t + 0.1);
        break;
      }
      case 'ratchet': {
        for (let i = 0; i < 5; i++) {
          const at = t + i * 0.016;
          const f = filt('bandpass', 2400 + i * 220, 6, out);
          const g = env(f, 0.11, at, 0.001, 0.012);
          noise(g, at, at + 0.02);
        }
        break;
      }
    }

    this.reap(srcs, nodes);
  }

  duck(ms: number): void {
    const ctx = this.ctx;
    const bus = this.musicGain;
    if (!ctx || !bus) return;
    const t = ctx.currentTime;
    const back = Math.max(0.05, ms / 1000);
    bus.gain.cancelScheduledValues(t);
    bus.gain.setValueAtTime(bus.gain.value, t);
    bus.gain.linearRampToValueAtTime(this.musicVolume * 0.22, t + 0.04);
    bus.gain.linearRampToValueAtTime(this.musicVolume, t + 0.04 + back);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== 0) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }
    for (const id of this.retireTimers) window.clearTimeout(id);
    this.retireTimers.clear();
    this.stopSpeedNoise();
    const ctx = this.ctx;
    if (this.voice) {
      for (const n of this.voice.nodes) n.disconnect();
      this.voice = null;
    }
    this.master?.disconnect();
    this.musicGain?.disconnect();
    this.sfxGain?.disconnect();
    this.master = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.noiseBuf = null;
    this.lastSfx.clear();
    this.unlockedFlag = false;
    this.ctx = null;
    if (ctx) void ctx.close().catch(() => undefined);
  }

  // ---------------------------------------------------------------- internals

  private async doUnlock(): Promise<void> {
    if (!AC) return;
    let ctx = this.ctx;
    if (!ctx) {
      ctx = new AC();
      this.ctx = ctx;
      this.buildGraph(ctx);
    }
    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
      } catch {
        // iOS rejects resume outside a gesture; the caller will try again.
      }
    }
    // A single silent sample fully arms the output path on iOS.
    const prime = ctx.createBufferSource();
    prime.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const silent = ctx.createGain();
    silent.gain.value = 0;
    prime.connect(silent);
    silent.connect(ctx.destination);
    prime.start(ctx.currentTime);
    prime.stop(ctx.currentTime + 0.02);
    prime.onended = () => {
      prime.onended = null;
      prime.disconnect();
      silent.disconnect();
    };

    this.unlockedFlag = ctx.state === 'running';
    if (!this.unlockedFlag) return;

    const pending = this.pending;
    if (pending) this.startMusic(pending);
    if (this.speedTarget > 0) this.setSpeedRatio(this.speedTarget);
  }

  private buildGraph(ctx: AudioContext): void {
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 26;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.22;
    comp.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.value = this.mutedFlag ? 0 : MASTER_LEVEL;
    master.connect(comp);

    const music = ctx.createGain();
    music.gain.value = this.musicVolume;
    music.connect(master);

    const sfx = ctx.createGain();
    sfx.gain.value = 0.9;
    sfx.connect(master);

    this.master = master;
    this.musicGain = music;
    this.sfxGain = sfx;
  }

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuf) return this.noiseBuf;
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
    return buf;
  }

  private reap(sources: AudioScheduledSourceNode[], nodes: AudioNode[]): void {
    if (sources.length === 0) {
      for (const n of nodes) n.disconnect();
      return;
    }
    let left = sources.length;
    for (const s of sources) {
      s.onended = () => {
        s.onended = null;
        s.disconnect();
        left -= 1;
        if (left === 0) for (const n of nodes) n.disconnect();
      };
    }
  }

  private createVoice(theme: Theme, ctx: AudioContext): MusicVoice {
    const spec = theme.music;
    const nodes: AudioNode[] = [];

    const out = ctx.createGain();
    out.gain.value = SILENT;
    out.connect(this.musicGain as GainNode);
    nodes.push(out);

    const dry = ctx.createGain();
    dry.connect(out);
    nodes.push(dry);

    const send = ctx.createGain();
    const delay = ctx.createDelay(1.5);
    delay.delayTime.value = Math.min(1.4, 30 / Math.max(40, spec.bpm));
    const fb = ctx.createGain();
    fb.gain.value = 0.25 * spec.space;
    const wet = ctx.createGain();
    wet.gain.value = 0.45 * spec.space;
    send.connect(delay);
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(out);
    nodes.push(send, delay, fb, wet);

    const padFilter = ctx.createBiquadFilter();
    padFilter.type = 'lowpass';
    padFilter.frequency.value = padCutoff(this.intensityNow);
    padFilter.Q.value = 0.7;
    padFilter.connect(dry);
    nodes.push(padFilter);

    const leadGain = ctx.createGain();
    leadGain.gain.value = leadLevel(this.intensityNow);
    leadGain.connect(dry);
    leadGain.connect(send);
    nodes.push(leadGain);

    return { themeId: theme.id, spec, out, dry, send, padFilter, leadGain, nodes };
  }

  private retireVoice(voice: MusicVoice, ctx: AudioContext, fade: number): void {
    const now = ctx.currentTime;
    const g = voice.out.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(g.value, SILENT), now);
    g.linearRampToValueAtTime(0, now + fade);
    const id = window.setTimeout(() => {
      this.retireTimers.delete(id);
      for (const n of voice.nodes) n.disconnect();
    }, fade * 1000 + 150);
    this.retireTimers.add(id);
  }

  private tick = (): void => {
    const ctx = this.ctx;
    const voice = this.voice;
    if (!ctx || !voice) return;
    if (ctx.state !== 'running') {
      // Suspended by the OS. Ask for it back and skip this tick, rather than
      // silently giving up on audio for the rest of the session.
      this.resume();
      this.nextStepTime = 0;
      return;
    }
    // Coming back from a suspend leaves the clock stale.
    if (this.nextStepTime === 0) this.nextStepTime = ctx.currentTime + 0.05;

    this.intensityNow += (this.intensityTarget - this.intensityNow) * 0.18;

    const spec = voice.spec;
    const stepDur = 15 / Math.max(40, spec.bpm);
    const now = ctx.currentTime;
    // A backgrounded tab can leave the clock far behind; resync rather than flood.
    if (this.nextStepTime < now - 0.4) this.nextStepTime = now + 0.02;

    const horizon = now + LOOKAHEAD;
    let guard = 0;
    while (this.nextStepTime < horizon && guard < 64) {
      guard += 1;
      this.scheduleStep(ctx, voice, this.nextStepTime, stepDur);
      this.nextStepTime += stepDur;
      this.step += 1;
      if (this.step >= 16) {
        this.step = 0;
        this.bar += 1;
      }
    }
  };

  private scheduleStep(ctx: AudioContext, voice: MusicVoice, t: number, stepDur: number): void {
    const spec = voice.spec;
    const step = this.step;
    const bar = this.bar;
    const intensity = this.intensityNow;
    const prog = spec.progression;
    const chord = prog.length > 0 ? prog[bar % prog.length] ?? 0 : 0;
    const chordMul = Math.pow(2, chord / 12);

    if ((spec.kick[step] ?? 0) > 0) this.kick(ctx, voice, t);
    else if (intensity > 0.8 && step === 14) this.kick(ctx, voice, t, 0.6);

    if ((spec.snare[step] ?? 0) > 0) this.snare(ctx, voice, t);
    else if (intensity > 0.65 && step % 8 === 7) this.snare(ctx, voice, t, 0.3);

    if ((spec.hat[step] ?? 0) > 0) this.hat(ctx, voice, t, step % 4 === 0 ? 1 : 0.6);
    else if (intensity > 0.55 && step % 2 === 1) this.hat(ctx, voice, t, 0.35);

    if (step % 4 === 0) this.bass(ctx, voice, t, (spec.root / 2) * chordMul, stepDur * 4);

    if (step === 0) this.pad(ctx, voice, t, spec.root * 2 * chordMul, stepDur * 16);

    if (step % 2 === 0 && intensity > 0.32) {
      const slot = (bar * 8 + (step >> 1)) % LEAD_PATTERN.length;
      const degree = (LEAD_PATTERN[slot] ?? 0) + (bar % 4 === 3 ? 2 : 0);
      const freq = scaleFreq(spec.root * 4 * chordMul, spec.scale, degree);
      this.lead(ctx, voice, t, freq, stepDur * 1.8);
    }
  }

  private kick(ctx: AudioContext, voice: MusicVoice, t: number, level = 1): void {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.85 * level, t);
    g.gain.exponentialRampToValueAtTime(SILENT, t + 0.22);
    g.connect(voice.dry);
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.09);
    o.connect(g);
    o.start(t);
    o.stop(t + 0.26);
    this.reap([o], [g]);
  }

  private snare(ctx: AudioContext, voice: MusicVoice, t: number, level = 1): void {
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 0.9;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.32 * level, t);
    ng.gain.exponentialRampToValueAtTime(SILENT, t + 0.12);
    bp.connect(ng);
    ng.connect(voice.dry);
    ng.connect(voice.send);
    const n = ctx.createBufferSource();
    const buf = this.noiseBuffer(ctx);
    n.buffer = buf;
    n.connect(bp);
    n.start(t, Math.random() * Math.max(0.001, buf.duration - 0.5));
    n.stop(t + 0.18);

    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.16 * level, t);
    bg.gain.exponentialRampToValueAtTime(SILENT, t + 0.09);
    bg.connect(voice.dry);
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(185, t);
    o.connect(bg);
    o.start(t);
    o.stop(t + 0.14);

    this.reap([n, o], [bp, ng, bg]);
  }

  private hat(ctx: AudioContext, voice: MusicVoice, t: number, level: number): void {
    const decay = level >= 1 ? 0.045 : 0.025;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 7000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.16 * level, t);
    g.gain.exponentialRampToValueAtTime(SILENT, t + decay);
    hp.connect(g);
    g.connect(voice.dry);
    const n = ctx.createBufferSource();
    const buf = this.noiseBuffer(ctx);
    n.buffer = buf;
    n.connect(hp);
    n.start(t, Math.random() * Math.max(0.001, buf.duration - 0.5));
    n.stop(t + decay + 0.02);
    this.reap([n], [hp, g]);
  }

  private bass(ctx: AudioContext, voice: MusicVoice, t: number, freq: number, dur: number): void {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(900, t);
    lp.frequency.exponentialRampToValueAtTime(260, t + dur * 0.7);
    lp.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENT, t);
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.012);
    g.gain.exponentialRampToValueAtTime(SILENT, t + dur * 0.9);
    lp.connect(g);
    g.connect(voice.dry);
    const o = ctx.createOscillator();
    o.type = voice.spec.bass;
    o.frequency.setValueAtTime(freq, t);
    o.connect(lp);
    o.start(t);
    o.stop(t + dur);
    this.reap([o], [lp, g]);
  }

  private pad(ctx: AudioContext, voice: MusicVoice, t: number, base: number, dur: number): void {
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENT, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + dur * 0.25);
    g.gain.setValueAtTime(0.05, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(SILENT, t + dur);
    g.connect(voice.padFilter);

    const oscs: OscillatorNode[] = [];
    for (const degree of [0, 2, 4]) {
      const freq = scaleFreq(base, voice.spec.scale, degree);
      for (const detune of [-7, 7]) {
        const o = ctx.createOscillator();
        o.type = voice.spec.pad;
        o.frequency.setValueAtTime(freq, t);
        o.detune.setValueAtTime(detune, t);
        o.connect(g);
        o.start(t);
        o.stop(t + dur + 0.05);
        oscs.push(o);
      }
    }
    this.reap(oscs, [g]);
  }

  private lead(ctx: AudioContext, voice: MusicVoice, t: number, freq: number, dur: number): void {
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENT, t);
    g.gain.exponentialRampToValueAtTime(0.9, t + 0.01);
    g.gain.exponentialRampToValueAtTime(SILENT, t + dur);
    g.connect(voice.leadGain);
    const o = ctx.createOscillator();
    o.type = voice.spec.lead;
    o.frequency.setValueAtTime(freq, t);
    o.connect(g);
    o.start(t);
    o.stop(t + dur + 0.05);
    this.reap([o], [g]);
  }

  private startSpeedNoise(ctx: AudioContext): void {
    const master = this.master;
    if (!master || this.speedSrc) return;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 220;
    lp.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.value = 0;
    lp.connect(g);
    g.connect(master);
    const s = ctx.createBufferSource();
    s.buffer = this.noiseBuffer(ctx);
    s.loop = true;
    s.connect(lp);
    s.start(ctx.currentTime);
    this.speedSrc = s;
    this.speedFilter = lp;
    this.speedGain = g;
  }

  private stopSpeedNoise(): void {
    const s = this.speedSrc;
    const lp = this.speedFilter;
    const g = this.speedGain;
    this.speedSrc = null;
    this.speedFilter = null;
    this.speedGain = null;
    if (!s) return;
    const ctx = this.ctx;
    const at = ctx ? ctx.currentTime + 0.05 : 0;
    if (g && ctx) {
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.linearRampToValueAtTime(0, at);
    }
    s.onended = () => {
      s.onended = null;
      s.disconnect();
      lp?.disconnect();
      g?.disconnect();
    };
    try {
      s.stop(at);
    } catch {
      // Already stopped.
    }
  }
}
